import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { useRef } from 'react';

export default function AboutPage() {
  const formRef = useRef<HTMLFormElement>(null);
  const [formStatus, setFormStatus] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleContactSubmit = useCallback(async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setFormStatus(null);

    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypotValue = (formData.get('website_alt') as string || '').trim();
    if (honeypotValue) {
      setFormStatus({ type: 'success', message: '感谢您的留言，我们会尽快回复！' });
      form.reset();
      setIsSubmitting(false);
      return;
    }

    formData.delete('website_alt');

    try {
      const response = await fetch(form.action, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus({ type: 'success', message: '感谢您的留言，我们会尽快回复！' });
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || '提交失败，请稍后重试。';
        setFormStatus({ type: 'error', message: serverMsg });
      }
    } catch {
      setFormStatus({ type: 'error', message: '网络错误，请检查连接后重试。' });
    } finally {
      setIsSubmitting(false);
    }
  }, []);

  const structuredData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "AboutPage",
        name: "关于韩漫网 - 韩国漫画在线阅读平台",
        description: "韩漫网是专业的韩国漫画在线阅读平台，提供最新、最热、最好看的免费韩国漫画资源。",
        url: "https://www.hanmanwang.com/about",
        isPartOf: { "@id": "https://www.hanmanwang.com/#website" },
      },
      {
        "@type": "Organization",
        name: "韩漫网",
        url: "https://www.hanmanwang.com",
        description: "专业的韩国漫画在线阅读平台，致力于为用户提供优质的漫画阅读体验。",
        contactPoint: {
          "@type": "ContactPoint",
          telephone: "400-888-6666",
          contactType: "customer service",
          availableLanguage: "Chinese",
        },
        address: {
          "@type": "PostalAddress",
          streetAddress: "建国路88号",
          addressLocality: "北京",
          addressRegion: "朝阳区",
          addressCountry: "CN",
        },
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: "https://www.hanmanwang.com" },
          { "@type": "ListItem", position: 2, name: "关于我们" },
        ],
      },
    ],
  };

  const milestones = [
    { year: '2024年6月', event: '韩漫网正式上线，首批上线500+部漫画' },
    { year: '2024年9月', event: '日活跃用户突破10万，建立专业翻译团队' },
    { year: '2024年12月', event: '漫画资源突破5000部，覆盖全品类' },
    { year: '2025年3月', event: '推出移动端优化版本，阅读体验全面升级' },
    { year: '2025年8月', event: '漫画资源突破10000部，日活用户突破50万' },
    { year: '2026年1月', event: '成为国内知名的韩国漫画阅读平台' },
  ];

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
      />
      <ScrollToTop />
      <Navbar />
      <main className="min-h-screen pt-16 bg-background-50">
        <div className="max-w-5xl mx-auto px-4 md:px-6 py-8">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: '关于我们' },
            ]}
          />

          <section className="mb-14">
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 mb-6">
              关于韩漫网
            </h1>
            <div className="bg-background-100 rounded-2xl p-6 md:p-8">
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                <strong className="text-foreground-900">韩漫网</strong>创建于2024年，是国内领先的<strong>韩国漫画</strong>在线阅读平台。我们致力于为中文读者提供最新、最全、最高质量的<strong>韩国漫画</strong>资源，让每一位漫画爱好者都能轻松享受优质的<strong>在线漫画</strong>阅读体验。
              </p>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                我们的团队由一群热爱漫画的年轻人组成，包括专业的韩语翻译团队、经验丰富的编辑团队和技术过硬的开发团队。我们每天从韩国各大漫画平台精选优质内容，经过专业翻译和精心排版后，第一时间呈现给国内读者。
              </p>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                韩漫网秉持<strong>"让好漫画触手可及"</strong>的理念，坚持免费阅读模式，不设置任何付费墙。我们相信，好的漫画应该被更多人看到，这也是我们不断前行的动力源泉。
              </p>
            </div>
          </section>

          <section className="mb-14">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-6">
              品牌历程
            </h2>
            <div className="relative">
              <div className="absolute left-4 md:left-6 top-0 bottom-0 w-0.5 bg-background-200"></div>
              <div className="space-y-6">
                {milestones.map((item, idx) => (
                  <div
                    key={idx}
                    className="relative flex items-start gap-4 md:gap-6 pl-10 md:pl-14 animate-fade-in"
                    style={{ animationDelay: `${idx * 0.1}s` }}
                  >
                    <div className="absolute left-3 md:left-[21px] w-3 h-3 rounded-full bg-primary-500 ring-4 ring-background-50 mt-1.5"></div>
                    <div className="bg-background-100 rounded-xl p-4 md:p-5 flex-1">
                      <span className="text-sm font-semibold text-primary-500 whitespace-nowrap">{item.year}</span>
                      <p className="text-sm md:text-base text-foreground-700 mt-1">{item.event}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="mb-14">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-6">
              用户口碑
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
              {[
                {
                  name: '小李',
                  role: '大学生',
                  comment: '韩漫网真的太良心了！更新速度超快，翻译质量也很高。每天必刷的网站，推荐给了好多同学！',
                  rating: 5,
                },
                {
                  name: '阿杰',
                  role: '上班族',
                  comment: '工作之余追漫画是我最大的放松方式。韩漫网资源特别全，基本上想看的韩国漫画都能找到，省去了到处找资源的麻烦。',
                  rating: 5,
                },
                {
                  name: '小雪',
                  role: '自由职业者',
                  comment: '用了很多漫画平台，韩漫网是体验最好的。页面干净没有广告干扰，加载速度也快，阅读体验一级棒！',
                  rating: 5,
                },
              ].map((review, idx) => (
                <div
                  key={idx}
                  className="bg-background-100 rounded-xl p-5 md:p-6 animate-fade-in"
                  style={{ animationDelay: `${idx * 0.1}s` }}
                >
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: review.rating }, (_, i) => (
                      <span key={i} className="w-4 h-4 flex items-center justify-center text-accent-500">
                        <i className="ri-star-fill text-sm"></i>
                      </span>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                    "{review.comment}"
                  </p>
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-secondary-200 flex items-center justify-center">
                      <span className="text-xs font-semibold text-secondary-700">{review.name[0]}</span>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground-800">{review.name}</div>
                      <div className="text-xs text-foreground-500">{review.role}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className="mb-14">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-6">
              联系我们
            </h2>
            <div className="flex flex-col lg:flex-row gap-8">
              <div className="flex-1 bg-background-100 rounded-2xl p-6 md:p-8">
                <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-4">联系方式</h3>
                <ul className="space-y-4">
                  <li className="flex items-start gap-3">
                    <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-full bg-primary-100">
                      <i className="ri-mail-line text-lg text-primary-500"></i>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground-800">电子邮箱</div>
                      <div className="text-sm text-foreground-600">contact@hanmanwang.com</div>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-full bg-primary-100">
                      <i className="ri-phone-line text-lg text-primary-500"></i>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground-800">客服电话</div>
                      <div className="text-sm text-foreground-600">400-888-6666</div>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-full bg-primary-100">
                      <i className="ri-map-pin-line text-lg text-primary-500"></i>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground-800">公司地址</div>
                      <div className="text-sm text-foreground-600">北京市朝阳区建国路88号 SOHO现代城A座</div>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-full bg-primary-100">
                      <i className="ri-time-line text-lg text-primary-500"></i>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground-800">工作时间</div>
                      <div className="text-sm text-foreground-600">周一至周五 9:00 - 18:00</div>
                    </div>
                  </li>
                </ul>
              </div>

              <div className="flex-1 bg-background-100 rounded-2xl p-6 md:p-8">
                <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-4">在线留言</h3>
                <form
                  ref={formRef}
                  onSubmit={handleContactSubmit}
                  action="https://readdy.ai/api/form/d9ork28senbnioleskk0"
                  className="space-y-4"
                  data-readdy-form="true"
                >
                  <input
                    type="text"
                    name="website_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />
                  <div>
                    <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      姓名 <span className="text-primary-500">*</span>
                    </label>
                    <input
                      id="contact-name"
                      type="text"
                      name="name"
                      required
                      className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      邮箱 <span className="text-primary-500">*</span>
                    </label>
                    <input
                      id="contact-email"
                      type="email"
                      name="email"
                      required
                      className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      留言内容 <span className="text-primary-500">*</span>
                    </label>
                    <textarea
                      id="contact-message"
                      name="message"
                      required
                      maxLength={500}
                      rows={4}
                      className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all resize-none"
                      placeholder="请输入您的留言内容（500字以内）"
                    ></textarea>
                  </div>

                  {formStatus && (
                    <div
                      className={`text-sm p-3 rounded-lg ${
                        formStatus.type === 'success'
                          ? 'bg-accent-100 text-accent-700'
                          : 'bg-primary-100 text-primary-700'
                      }`}
                    >
                      {formStatus.message}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full px-6 py-3 bg-primary-500 hover:bg-primary-600 disabled:bg-foreground-300 text-background-50 font-semibold rounded-full transition-all duration-300 whitespace-nowrap"
                  >
                    {isSubmitting ? '提交中...' : '发送留言'}
                  </button>
                </form>
              </div>
            </div>
          </section>
        </div>
      </main>
      <Footer />
    </>
  );
}